import { useState, useEffect } from "react";
import { Users, UtensilsCrossed, MessageSquare, Tags, ClipboardList, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const StatCard = ({ title, value, icon: Icon, color, delay }) => (
  <Card className={`bg-card border-border card-shadow animate-fade-in-up stagger-${delay} hover:border-primary/30 transition-colors`}>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Oswald, sans-serif', color }}>{value}</p>
        </div>
        <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

function fmtBRL(v) {
  const num = parseFloat(v) || 0;
  return `R$ ${num.toFixed(2).replace(".", ",")}`;
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get("/dashboard/stats");
        setStats(res.data);
      } catch (err) { console.error("Error fetching stats:", err); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-secondary rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-28 bg-card border border-border rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  return (
    <div className="space-y-8">
      <div className="animate-fade-in-up stagger-1">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Painel
        </h1>
        <p className="text-muted-foreground mt-2">
          Bem-vindo, <span className="text-foreground font-medium">{user.name || "Admin"}</span>. Dados em tempo real do Supabase.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stats-grid">
        <StatCard title="Pedidos" value={stats?.total_orders || 0} icon={ClipboardList} color="#FF5722" delay={1} />
        <StatCard title="Clientes" value={stats?.total_customers || 0} icon={Users} color="#2196F3" delay={2} />
        <StatCard title="Itens Cardapio" value={stats?.total_menu_items || 0} icon={UtensilsCrossed} color="#FFC107" delay={3} />
        <StatCard title="Categorias" value={stats?.total_categories || 0} icon={Tags} color="#4CAF50" delay={4} />
        <StatCard title="Confirmados" value={stats?.confirmed_orders || 0} icon={MessageSquare} color="#9C27B0" delay={5} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Orders */}
        <Card className="bg-card border-border card-shadow animate-fade-in-up stagger-5">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-primary" />
              Pedidos Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_orders?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_orders.slice(0, 5).map((o) => (
                  <div key={o.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-secondary/40 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">
                      {(o.nomeCliente || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{o.nomeCliente || "-"}</p>
                      <p className="text-xs text-muted-foreground truncate">{o.descPedido || "-"}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-accent">{fmtBRL(o.Valor_Total)}</p>
                      <Badge className={`text-[9px] ${o.statusPedido === "confirmado" ? "bg-green-900/50 text-green-100" : "bg-secondary"} border-transparent`}>
                        {o.statusPedido || "-"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">Nenhum pedido ainda</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Customers */}
        <Card className="bg-card border-border card-shadow animate-fade-in-up stagger-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Clientes Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_customers?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_customers.slice(0, 5).map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-secondary/40 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-blue-900/30 flex items-center justify-center text-xs font-medium text-blue-400">
                      {(c.nomeCliente || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.nomeCliente || "-"}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {(c.telefoneCliente || "").replace("@s.whatsapp.net", "")}
                      </p>
                    </div>
                    {c.enderecoCliente && (
                      <p className="text-xs text-muted-foreground truncate max-w-[120px]">{c.enderecoCliente}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">Nenhum cliente ainda</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
